import {PurchaseConfig, Seller} from '../models/portal.models';
import { State, StateContext, Action, Selector } from '@ngxs/store';
import {
  SellerPurchaseConfig,
  SellerSalesPerson,
  SelectedSellerStore,
  UpdateSellers, AddSelectedSeller
} from '../actions/seller.action';
import { SellerService } from '../services/seller.service';
import { tap } from 'rxjs/operators';
import { SellerApi } from '../api/seller.api';
import { Injectable } from '@angular/core';

export class SellerStateModel {
  purchaseConfig?: PurchaseConfig;
}

@State<SellerStateModel>({
  name: 'seller'
})
@Injectable()
export class SellerState {
  @Selector()
  static purchaseConfig(state: SellerStateModel) {
    return state.purchaseConfig;
  }
  @Selector()
  static products(state: SellerStateModel) {
    return state.purchaseConfig.financeProducts;
  }
  @Selector()
  static sellerStores(state: SellerStateModel) {
    return state.purchaseConfig.salesPerson.sellers;
  }
  @Selector()
  static sellerId(state: SellerStateModel) {
    return state.purchaseConfig.sellerId;
  }
  @Selector()
  static sellerName(state: SellerStateModel) {
    return state.purchaseConfig.sellerName;
  }
  @Selector()
  static dealerNumber(state: SellerStateModel) {
    return state.purchaseConfig.dealerNumber;
  }
  @Selector()
  static sellerRoles(state: SellerStateModel) {
    return state.purchaseConfig.salesPerson.role.roleType;
  }
  @Selector()
  static salespersonPermissions(state: SellerStateModel){
    return state.purchaseConfig.salesPerson.role.permissions;
  }
  @Selector()
  static selectedSellerStore(state: SellerStateModel) {
    return state.purchaseConfig.salesPerson.sellers.find(
      s => s.sellerId === state.purchaseConfig.sellerId
    );
  }
  @Selector()
  static sellerGroupId(state: SellerStateModel) {
    return state.purchaseConfig.salesPerson.sellerGroupId;
  }
  @Selector()
  static selectedSellerGroupId(state: SellerStateModel) {
    return state.purchaseConfig.sellerGroupId;
  }
  constructor(private sellerService: SellerService) {}

  @Action(SellerPurchaseConfig)
  sellerPurchaseConfig(
    { getState, patchState }: StateContext<SellerStateModel>,
    { cardProgrammeCd }
  ) {
    const state = getState() || {};
    const request: SellerApi.purchaseConfig.Request = {
      sellerId: state.purchaseConfig.sellerId,
      cardProgrammeCd: cardProgrammeCd
    };
    return this.sellerService.getPurchaseConfig(request).pipe(
      tap(result => {
        patchState({
          purchaseConfig: {
            ...state.purchaseConfig,
            ...{
              sellerId: result.sellerId,
              allowDeposit: result.properties.depositFlag === 'Y',
              allowDelayedDelivery: result.properties.delayedDelivery === 'Y',
              merchantReference: result.properties.merchantReference === 'Y',
              delayedDeliveryDefault:
                result.properties.delayedDeliveryDefault === 'D',
              automateDelayedDelivery: result.properties.automateDelayedDelivery === 'Y',
              financeProducts: result.financeProducts,
              allowSaveForLater: false
            }
          }
        });
      })
    );
  }

  @Action(SellerSalesPerson)
  sellerSalesPerson({ getState, patchState }: StateContext<SellerStateModel>) {
    const state = getState() || {};

    return this.sellerService.getSalesPerson().pipe(
      tap(result => {
        patchState({
          purchaseConfig: {
            ...state.purchaseConfig,
            ...{ salesPerson: result }
          }
        });
      })
    );
  }

  @Action(UpdateSellers)
  updateSellers({ getState, patchState }: StateContext<SellerStateModel>) {
    const state = getState() || {};

    return this.sellerService
      .getSellers(state.purchaseConfig.salesPerson.sellerGroupId)
      .pipe(
        tap(result => {
          patchState({
            purchaseConfig: {
              ...state.purchaseConfig,
              salesPerson: {
                ...state.purchaseConfig.salesPerson,
                ...{ sellers: [...result] }
              }
            }
          });
        })
      );
  }

  @Action(SelectedSellerStore)
  sellerStore(
    { getState, patchState }: StateContext<SellerStateModel>,
    { storeId, storeName, dealerNumber, sellerGroupId }: SelectedSellerStore
  ) {
    const state = getState() || {};
    patchState({
      purchaseConfig: {
        ...state.purchaseConfig,
        ...{
          sellerId: storeId,
          sellerName: storeName,
          sellerGroupId: sellerGroupId,
          dealerNumber
        }
      }
    });
    return true;
  }

  @Action(AddSelectedSeller)
  updateSellersForSU(
    {getState, patchState}: StateContext<SellerStateModel>,
    selectedSeller: AddSelectedSeller
  ) {
    const seller: Seller = {
      sellerId: selectedSeller.storeId,
      dealerPerorgNo: selectedSeller.dealerNumber,
      sellerGroupId: selectedSeller.sellerGroupId,
      sellerName: selectedSeller.storeName,
      userPasswordId: null,
      allowedOriginationType: null,
      roles: null,
      salesPersonId: null
    };
    const state = getState() || {};
    patchState({
      purchaseConfig: {
        ...state.purchaseConfig,
        salesPerson: {
          ...state.purchaseConfig.salesPerson,
          ...{sellers: [seller]}
        }
      }
    });
  }
}
